"use client"

import { React, useEffect, useState } from "react"
import { useFetchCampaigns } from "../hooks/DataFetching/useFetchCampaigns"
import { useAuth0 } from "@auth0/auth0-react"
import { useCampaignFollows } from "../hooks/useCampaignFollows"
import CampaignModal from "./CampaignModal"
import { Card, CardContent } from "./ui/card"
import { Badge } from "./ui/badge"
import { Bell, BellOff, ThumbsUp, ThumbsDown, Clock, Calendar } from "lucide-react"

export const FetchCampaigns = ({ groupId, groupName, selectedCampaignId }) => {
  const { campaigns, loading, error, refresh } = useFetchCampaigns(groupId)
  const { user } = useAuth0()
  const { isFollowingCampaign, toggleCampaignFollow } = useCampaignFollows(user?.sub)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [openedCampaignId, setOpenedCampaignId] = useState(null)

  const handleCampaignClick = (campaign) => {
    setSelectedCampaign(campaign)
  }

  const closeModal = () => {
    setSelectedCampaign(null)
  }

  useEffect(() => {
    if (!selectedCampaignId || !campaigns.length) return
    if (String(openedCampaignId) === String(selectedCampaignId)) return

    const requestedCampaign = campaigns.find((campaign) => Number(campaign.id) === Number(selectedCampaignId))
    if (requestedCampaign && Number(selectedCampaign?.id) !== Number(requestedCampaign.id)) {
      setSelectedCampaign(requestedCampaign)
      setOpenedCampaignId(selectedCampaignId)
    }
  }, [campaigns, openedCampaignId, selectedCampaign?.id, selectedCampaignId])

  useEffect(() => {
    if (!selectedCampaign?.id) return
    const updatedCampaign = campaigns.find((campaign) => Number(campaign.id) === Number(selectedCampaign.id))
    if (updatedCampaign && updatedCampaign !== selectedCampaign) setSelectedCampaign(updatedCampaign)
  }, [campaigns, selectedCampaign])

  // Calculate vote ratio and format it
  const getVoteRatio = (campaign) => {
    // Use voteUp and voteDown instead of upVotes and downVotes
    const upVotes = campaign.voteUp || 0
    const downVotes = campaign.voteDown || 0
    const total = upVotes + downVotes

    if (total === 0) return { ratio: "0%", upVotes, downVotes, total }

    const percentage = Math.round((upVotes / total) * 100)
    return {
      ratio: `${percentage}%`,
      upVotes,
      downVotes,
      total,
      isPositive: percentage >= 50,
    }
  }

  // Get appropriate color based on vote ratio
  const getVoteRatioColor = (ratio) => {
    const percentage = Number.parseInt(ratio.ratio)
    if (percentage >= 75) return "bg-green-500 hover:bg-green-600"
    if (percentage >= 50) return "bg-blue-500 hover:bg-blue-600"
    if (percentage >= 25) return "bg-yellow-500 hover:bg-yellow-600"
    return "bg-red-500 hover:bg-red-600"
  }

  // Format date to be more readable
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  // Determine if a campaign is expiring soon (within 7 days)
  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false
    const expiration = new Date(expirationDate)
    const now = new Date()
    const daysUntilExpiration = Math.floor((expiration - now) / (1000 * 60 * 60 * 24))
    return daysUntilExpiration <= 7 && daysUntilExpiration >= 0
  }

  const contents = loading ? (
    <div className="flex justify-center items-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      <span className="ml-3 text-muted-foreground">Loading campaigns...</span>
    </div>
  ) : error ? (
    <div className="text-center py-8 bg-red-500/10 border border-red-300/30 rounded-lg">
      <p className="text-red-100 font-semibold">Ideas could not be loaded.</p>
      <p className="text-red-100/75 text-sm mt-2">Please refresh the page or try again in a moment.</p>
    </div>
  ) : (
    <div className="space-y-4 mt-4">
      {campaigns.length === 0 ? (
        <div className="text-center py-8 bg-muted/30 rounded-lg">
          <p className="text-muted-foreground">No campaigns found for this group.</p>
        </div>
      ) : (
        campaigns.map((campaign) => {
          const voteInfo = getVoteRatio(campaign)
          const ratioColor = getVoteRatioColor(voteInfo)
          const isFollowing = isFollowingCampaign(campaign.id)

          return (
            <Card
              key={campaign.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleCampaignClick(campaign)}
            >
              <CardContent className="p-0">
                <div className="flex items-center justify-between p-4">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="font-medium text-lg text-foreground">{campaign.title}</h3>
                      {isExpiringSoon(campaign.expiration) && (
                        <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-800 border-amber-200">
                          <Clock className="h-3 w-3 mr-1" /> Expiring Soon
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center text-sm text-muted-foreground/80 mt-1">
                      <span className="flex items-center mr-4">
                        <Calendar className="h-3 w-3 mr-1" />
                        Created: {formatDate(campaign.createdOn)}
                      </span>
                      {campaign.expiration && (
                        <span className="flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          Expires: {formatDate(campaign.expiration)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <button
                      type="button"
                      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition ${
                        isFollowing
                          ? "border-amber-300/60 bg-amber-300/15 text-amber-100"
                          : "border-cyan-300/35 bg-cyan-300/10 text-cyan-100 hover:bg-cyan-300/15"
                      }`}
                      onClick={(event) => {
                        event.stopPropagation()
                        toggleCampaignFollow({ ...campaign, groupName })
                      }}
                      aria-pressed={isFollowing}
                    >
                      {isFollowing ? <BellOff className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
                      {isFollowing ? "Following" : "Follow"}
                    </button>
                    <Badge className={`${ratioColor} text-white`}>{voteInfo.ratio} Approval</Badge>

                    <div className="flex items-center text-sm text-muted-foreground/90">
                      <span className="flex items-center mr-2">
                        <ThumbsUp className="h-3 w-3 mr-1 text-green-500" />
                        {voteInfo.upVotes}
                      </span>
                      <span className="flex items-center">
                        <ThumbsDown className="h-3 w-3 mr-1 text-red-500" />
                        {voteInfo.downVotes}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })
      )}
    </div>
  )

  return (
    <div className="campaign-list">
      <h2 className="text-xl font-semibold text-foreground py-2">Ideas & Campaigns</h2>
      {contents}
      <CampaignModal isOpen={!!selectedCampaign} onClose={closeModal} campaignData={selectedCampaign || {}} onVoteSaved={refresh} />
    </div>
  )
}

export default FetchCampaigns

